import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { seriesApi, iaApi, workoutApi } from '../services/api';

function pickTodayWorkout(list) {
  if (!list.length) return null;
  const base = list
    .filter((w) => ['push', 'pull', 'legs'].includes(w.slug))
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
  const pool = base.length ? base : list;
  let best = pool[0];
  let bestT = Infinity;
  for (const w of pool) {
    const t = w.lastCompletedAt ? new Date(w.lastCompletedAt).getTime() : 0;
    if (t < bestT) {
      bestT = t;
      best = w;
    }
  }
  return best;
}

function saveActiveWorkoutId(userId, workoutId) {
  sessionStorage.setItem(
    'iron_active_workout_id',
    JSON.stringify({ userId: String(userId), workoutId: workoutId != null ? String(workoutId) : '' })
  );
}

const HomePage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalSeries: 0,
    totalWorkouts: 0,
    avgEffort: 0,
    avgMood: 0,
  });
  const [workouts, setWorkouts] = useState([]);
  const [recentFeedbacks, setRecentFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const todayWorkout = useMemo(() => pickTodayWorkout(workouts), [workouts]);
  const aiOk = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('iron_ai_ok') === '1';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        await workoutApi.ensureDefaults().catch(() => {});
        const wr = await workoutApi.getAll();
        const wl = Array.isArray(wr.data) ? wr.data : [];
        setWorkouts(wl);

        // Fetch recent series for stats
        const seriesResponse = await seriesApi.getAll({ limit: 50 });
        const seriesPayload = seriesResponse?.data;
        const seriesList = Array.isArray(seriesPayload)
          ? seriesPayload
          : Array.isArray(seriesPayload?.data)
          ? seriesPayload.data
          : [];

        const totalSeries = seriesList.length;

        // Calculate average effort and mood from evaluations
        let totalEffort = 0;
        let totalMood = 0;
        let evalCount = 0;

        seriesList.forEach((series) => {
          if (Array.isArray(series?.evaluations) && series.evaluations.length > 0) {
            series.evaluations.forEach((evaluation) => {
              totalEffort += Number(evaluation?.effort) || 0;
              totalMood += Number(evaluation?.mood) || 0;
              evalCount++;
            });
          }
        });

        const avgEffort = evalCount > 0 ? (totalEffort / evalCount).toFixed(1) : 0;
        const avgMood = evalCount > 0 ? (totalMood / evalCount).toFixed(1) : 0;

        setStats({
          totalSeries,
          totalWorkouts: 0,
          avgEffort,
          avgMood,
        });

        // Fetch recent IA feedbacks (worker devolve array; Express pode devolver {data:[...]})
        const feedbackResponse = await iaApi.getHistory({ limit: 5 });
        const fbPayload = feedbackResponse?.data;
        const fbList = Array.isArray(fbPayload)
          ? fbPayload
          : Array.isArray(fbPayload?.data)
          ? fbPayload.data
          : [];
        setRecentFeedbacks(fbList);
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-iron-border border-t-iron-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="font-display text-4xl tracking-wider text-iron-accent">
          Olá, {user?.name || 'atleta'}
        </h1>
        <p className="mt-2 font-mono text-xs uppercase tracking-widest text-iron-muted">
          Painel · IA · consistência
        </p>
      </div>

      {!aiOk && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 font-mono text-xs text-amber-200">
          Motor de IA sem confirmação recente. Verifica a chave no{' '}
          <Link to="/profile" className="text-iron-accent underline">
            Perfil
          </Link>{' '}
          ou o servidor.
        </div>
      )}

      {todayWorkout && (
        <div className="card border-iron-accent/35">
          <h2 className="section-label mb-2">Plano sugerido hoje</h2>
          <p className="font-display text-2xl text-iron-text tracking-wide">{todayWorkout.name}</p>
          {todayWorkout.description && (
            <p className="mt-1 font-mono text-xs text-iron-muted line-clamp-2">{todayWorkout.description}</p>
          )}
          <Link
            to="/workout"
            onClick={() => user?.id && saveActiveWorkoutId(user.id, todayWorkout.id)}
            className="btn-primary mt-4 block w-full py-3 text-center"
          >
            Iniciar este treino
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-iron-border bg-iron-border">
        <div className="bg-iron-surface p-4 text-center">
          <div className="font-display text-3xl text-iron-accent">{stats.totalSeries}</div>
          <div className="font-mono text-[9px] uppercase tracking-wide text-iron-muted">Séries</div>
        </div>
        <div className="bg-iron-surface p-4 text-center">
          <div className="font-display text-3xl text-iron-info">{stats.avgEffort}</div>
          <div className="font-mono text-[9px] uppercase tracking-wide text-iron-muted">Esforço médio</div>
        </div>
        <div className="bg-iron-surface p-4 text-center">
          <div className="font-display text-3xl text-iron-success">{stats.avgMood}</div>
          <div className="font-mono text-[9px] uppercase tracking-wide text-iron-muted">Ânimo médio</div>
        </div>
        <div className="bg-iron-surface p-4 text-center">
          <div className="font-display text-2xl text-iron-text">{user?.level}</div>
          <div className="font-mono text-[9px] uppercase tracking-wide text-iron-muted">Nível</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="section-label mb-4 block">Atalhos</h2>
        <div className="grid grid-cols-1 gap-3">
          <Link
            to="/workout"
            className="flex items-center justify-between rounded-xl border border-iron-border bg-iron-surface p-4 transition-colors hover:border-iron-accent"
          >
            <div>
              <h3 className="font-medium text-iron-text">Registrar série</h3>
              <p className="font-mono text-[10px] text-iron-muted">Treino em tempo real</p>
            </div>
            <span className="font-display text-iron-accent">→</span>
          </Link>

          <Link
            to="/plan"
            className="flex items-center justify-between rounded-xl border border-iron-border bg-iron-surface p-4 transition-colors hover:border-iron-accent"
          >
            <div>
              <h3 className="font-medium text-iron-text">Plano com IA</h3>
              <p className="font-mono text-[10px] text-iron-muted">Gera e cria treino</p>
            </div>
            <span className="font-display text-iron-accent">✦</span>
          </Link>

          <Link
            to="/history"
            className="flex items-center justify-between rounded-xl border border-iron-border bg-iron-surface p-4 transition-colors hover:border-iron-accent"
          >
            <div>
              <h3 className="font-medium text-iron-text">Histórico</h3>
              <p className="font-mono text-[10px] text-iron-muted">Séries anteriores</p>
            </div>
            <span className="font-display text-iron-accent">≡</span>
          </Link>

          <Link
            to="/profile"
            className="flex items-center justify-between rounded-xl border border-iron-border bg-iron-surface p-4 transition-colors hover:border-iron-accent"
          >
            <div>
              <h3 className="font-medium text-iron-text">Perfil e IA</h3>
              <p className="font-mono text-[10px] text-iron-muted">Estado físico · API keys</p>
            </div>
            <span className="font-display text-iron-accent">☺</span>
          </Link>
        </div>
      </div>

      {/* Recent Feedback */}
      {recentFeedbacks.length > 0 && (
        <div className="card">
          <h2 className="section-label mb-4 block">Últimos feedbacks IA</h2>
          <div className="space-y-3">
            {recentFeedbacks.map((feedback, index) => (
              <div key={index} className="rounded-xl border border-iron-border bg-iron-surface p-4">
                <div className="mb-2 font-mono text-[10px] text-iron-info">
                  {feedback.timestamp ? new Date(feedback.timestamp).toLocaleString('pt-BR') : ''}
                </div>
                <p className="whitespace-pre-wrap text-sm text-iron-text">{feedback.response}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center font-mono text-[10px] text-iron-muted">
        <Link to="/workout" className="text-iron-accent hover:underline">
          Iniciar série
        </Link>
      </div>
    </div>
  );
};

export default HomePage;
